import { CalendarPrintPageBase } from './CalendarPrintPage.base';
import type {
    CalendarPrintPageProps,
    CalendarPrintPageStyleProps,
    CalendarPrintPageStyles,
} from './CalendarPrintPage.types';
import { styled } from '@fluentui/react/lib/Utilities';
import { getPalette } from 'owa-theme';
export type { CalendarPrintPageProps };

/** The default style of CalendarPrintPage */
export function getStyles(styleProps: CalendarPrintPageStyleProps): CalendarPrintPageStyles {
    const { preview, contentCanGrow } = styleProps;
    const themeSymbols = getPalette();
    return {
        root: [
            'ms-CalendarPrintPage',
            {
                position: 'relative',
                // If the content can grow, let normal block layout decide the size of each child div.
                // If the content must fill the page, use flex layout to let pageBody grow to full size.
                display: contentCanGrow ? 'block' : 'flex',
                flexDirection: 'column',

                // This is the size of the page as it was in jsMVVM.
                // Perhaps we can use paper-size and allow user to control the size before printing.
                width: '888px',
                padding: 0,
                pageBreakAfter: 'always',
            },

            // Lock the size of the page during print-preview or in pages where the body stays in one page.
            (preview || !contentCanGrow) && {
                height: '1130px',
            },

            // During preview, allow the user to scroll the content if it grows large.
            preview &&
                contentCanGrow && {
                    overflowY: 'scroll',
                },

            styleProps.className,
        ],
        inputBlocker: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 99,
        },
        pageHeader: {
            position: 'relative',
            paddingLeft: 10,
            selectors: {
                '& > .ms-DatePicker': {
                    marginLeft: '10px',
                },
                '& .ms-DatePicker-dayBackground, & .ms-DatePicker-day--highlighted': {
                    // Override the background color of highlighted days.
                    // Need both selectors as they both set the color, and need !important
                    // because the color gets applied by the private class after the public class.
                    backgroundColor: 'inherit !important',
                },
                '& .ms-DatePicker-monthComponents': {
                    // Kill the container that shows the up/down buttons.
                    display: 'none',
                },
                '& .ms-DatePicker-header': {
                    // Centralize the remaining header content.
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                },
                '& .ms-DatePicker-monthAndYear': {
                    // Remove padding and marging from the header text, so it centralizes properly.
                    marginLeft: 0,
                    marginRight: 0,
                    padding: 0,
                },
            },
        },
        calendarHeader: {
            flexGrow: 1,
            padding: '20px 0px',
        },
        previewHeaders: {
            paddingBottom: '5px',
            fontWeight: '700',
            display: 'flex',
            whiteSpace: 'pre-wrap',
            flexWrap: 'wrap',
            selectors: {
                '& div': {
                    minWidth: 0,
                    overflow: 'hidden',
                    wordBreak: 'break-all',
                },
            },
        },
        icon: {
            position: 'relative',
            top: '4px',
            paddingRight: '8px',
        },
        pageBody: [
            {
                position: 'relative',
                flexGrow: 1,
                overflowX: 'hidden',
                overflowY: 'hidden',
            },
            contentCanGrow && {
                overflowY: 'auto',
            },
        ],
        eventsContainer: [
            (!contentCanGrow || preview) && {
                display: 'flex',
                flexDirection: 'row',
                flex: '1 1 auto',
                minHeight: 0,
            },
        ],
        notesSection: {
            width: '50%',
            height: '100%',
            border: 'solid 1px',
            borderColor: themeSymbols.neutralTertiaryAlt,
        },
    };
}

/** The default styled version of CalendarPrintPage */
export const CalendarPrintPage = styled<
    CalendarPrintPageProps,
    CalendarPrintPageStyleProps,
    CalendarPrintPageStyles
>(CalendarPrintPageBase, getStyles);
